import { useFormik } from "formik";
import * as Yup from "yup";
import classNames from "classnames";

import AscModal from "../AscModal/AscModal";
import AscButton from "../AscButton/AscButton";
import AscHeading from "../AscTypography/AscHeading/AscHeading";
import AscTextInput from "../AscInputFields/AscTextInput/AscTextInput";
import AscCheckBox from "../AscInputFields/AscCheckBox/AscCheckBox";

import styles from "./AscModalAddCertificate.module.scss";
import { useEffect } from "react";

interface SkillsCategoryTypes {
  id: string;
  name: string;
}

interface CertificatesDataTypes {
  id?: string;
  title: string;
  categories?: SkillsCategoryTypes[];
  updatedAt?: string;
}

export interface AscModalAddCertificatePropsI {
  visible: boolean;
  header: string;
  cardData?: CertificatesDataTypes;
  skillsData?: SkillsCategoryTypes[];
  onSubmit: (values: { title: string; categories?: string[] }) => void;
  onClose?: () => void;
}

function AscModalAddCertificate({
  visible,
  header,
  cardData,
  skillsData,
  onSubmit,
  onClose,
}: AscModalAddCertificatePropsI) {
  const formik = useFormik({
    initialValues: {
      title: cardData?.title || "",
      categories: cardData?.categories?.map(({ id }) => id) || [],
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title is required."),
    }),
    onSubmit: onSubmit,
  });

  const handleCheck = (id: string) => {
    if (formik.values?.categories?.includes(id)) {
      formik.setFieldValue(
        "categories",
        formik.values.categories.filter((category) => category !== id)
      );
    } else {
      formik.setFieldValue("categories", [...formik.values.categories, id]);
    }
  };

  useEffect(() => {
    formik.setValues({
      title: cardData?.title || "",
      categories: cardData?.categories?.map(({ id }) => id) || [],
    });
  }, [cardData]);

  return (
    <AscModal
      visible={visible}
      modalClassName={styles.modalClassName}
      withBackground={false}
      onClose={onClose}
    >
      <div className={styles.ground}>
        <AscHeading className={styles.header}>{header}</AscHeading>

        <div className={styles.contentGround}>
          <div className={styles.topNav}>
            <AscHeading className={styles.navTitle} level={6}>
              Details
            </AscHeading>
          </div>

          <div className={styles.content}>
            <div className={styles.inputField}>
              <label>Title</label>
              <AscTextInput
                className={classNames(styles.input, {
                  [styles.errorInput]:
                    formik.errors.title && formik.touched.title,
                })}
                id="title"
                name="title"
                fullWidth={true}
                placeholder="Title of certificate"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.title}
              />
              {formik.errors.title &&
                formik.touched.title &&
                typeof formik.errors.title === "string" && (
                  <div className={styles.fieldError}>{formik.errors.title}</div>
                )}
            </div>

            {skillsData ? (
              <div className={styles.inputField}>
                <label>Skills Categories</label>
                <div className={styles.skills}>
                  {skillsData.map((skill) => {
                    return (
                      <div key={skill.id} className={styles.skill}>
                        <AscCheckBox
                          id="categories"
                          name="categories"
                          checked={formik.values.categories?.includes(
                            skill?.id
                          )}
                          onChange={() => handleCheck(skill.id)}
                        />
                        <label>{skill.name}</label>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className={styles.buttonGround}>
          <AscButton
            onClick={() => formik?.handleSubmit()}
            type="submit"
            className={styles.button}
          >
            {cardData?.id ? "Save" : "Add"}
          </AscButton>
        </div>
      </div>
    </AscModal>
  );
}

export default AscModalAddCertificate;
